import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useMemo, useState } from 'react';
import {
  Container,
  FormLabel,
  Nav,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import { Placement } from 'react-bootstrap/esm/types';
import Avatar, { AvatarSize, AvatarStatus } from '../../../components/Avatar';
import GenericCard from '../../../components/GenericCards/GenericCards';
import { AuthStore } from '../../../providers/auth';
import {
  CASE_LIST_ROUTE,
  PARTICIPANT_ACCESS_ROUTE,
  DOCUMENT_SEARCH_ROUTE,
} from '../../../routes/types';
import { Profile } from '../../../types';

export type ProfilePopoverProps = React.PropsWithChildren<{
  data: Profile;
  placement?: Placement;
  currentPersona: string;
}>;

const FilerPopover = ({
  placement = 'bottom-end',
  data,
  children,
  currentPersona,
}: ProfilePopoverProps) => {
  const [, , logout, selectedPersona, changePersona] =
    useContext(AuthStore) || [];
  const [show, setShow] = useState(false);
  const [showPersonaMenu, setShowPersonaMenu] = useState(false);
  const handleToggle = () => {
    setShow((prev) => !prev);
    setShowPersonaMenu(false);
  };

  const handleTogglePersonaMenu = () => {
    setShowPersonaMenu((prev) => !prev);
  };

  const handleChange = (e: string | number) => {
    if (e.toString()) {
      changePersona(e.toString());
    }
  };

  const personasCategories = useMemo(() => {
    return data.personas?.reduce((categories: any, curr: any) => {
      let foundCategory = categories.find((category: any) => category === curr.category);
      if(!foundCategory) {
        categories.push(curr.category);
      }
      
      return categories;
    }, []);
  }, [data.personas]);

  const profilePopoverBody = (
    <Popover bsPrefix={'popover profile'}>
      <Popover.Body>
        <div className={'p-3'}>
          <section className={'d-flex align-items-center pb-7 border-bottom'}>
            <Avatar
              id={'ppo-1'}
              size={AvatarSize.MEDIUM}
              image={data.image}
              name={'Pat Simons'}
              status={AvatarStatus.ENABLED}
            />
            <div className="ms-7 d-flex flex-column align-items-start">
              <h5 className={'mb-2 fw-normal text-fs-6'}>{data.name}</h5>
              <span className={'fw-light text-fs-6'}>{data.email}</span>
            </div>
          </section>
          
          <section className="profile-nav-wrapper py-10 border-bottom">
            <Nav.Link
              href={`#${CASE_LIST_ROUTE}`}
              className={'py-4 px-0 text-fs-6 text-dark-gray fw-light'}
              onClick={handleToggle}
            >
              <div className={'d-flex align-items-center inner-nav-wrapper'}>
                <FontAwesomeIcon
                  icon={light('clipboard-list')}
                  className={'me-3'}
                />
                <span>{'Case List'}</span>
              </div>
            </Nav.Link>
            <Nav.Link href={`#${PARTICIPANT_ACCESS_ROUTE}`} onClick={handleToggle} className={'py-4 px-0 text-fs-6 text-dark-gray fw-light'}>
              <div className={'d-flex align-items-center inner-nav-wrapper'}>
                <FontAwesomeIcon icon={light('user-gear')} className={'me-4'} />
                <span>{'Participant Access'}</span>
              </div>
            </Nav.Link>
            <Nav.Link href={`#${DOCUMENT_SEARCH_ROUTE}`} onClick={handleToggle} className={'py-4 px-0 text-fs-6 text-dark-gray fw-light'}>
              <div className={'d-flex align-items-center inner-nav-wrapper'}>
                <FontAwesomeIcon icon={light('file-magnifying-glass')} className={'me-4'} />
                <span>{'Document Search'}</span>
              </div>
            </Nav.Link>
            
            {data.personas && data.personas.length > 1 && <Nav.Link
              className={`inner-menu-holder py-4 px-0 text-fs-6 text-dark-gray fw-light ${
                showPersonaMenu ? 'open' : ''
              }`}
              onClick={handleTogglePersonaMenu}
            >
              <div className={'d-flex align-items-center inner-nav-wrapper'}>
                <FontAwesomeIcon
                  icon={light('circle-user')}
                  className={'me-3'}
                />
                <span>
                  <span className="text-danger fw-bold me-1">
                    {'*Demo Only'}
                  </span>
                  {'Switch Persona'}
                </span>
                <FontAwesomeIcon
                  icon={light('chevron-right')}
                  className={'ms-auto'}
                />
              </div>
            </Nav.Link>}
            {data.personas && showPersonaMenu && data.personas.length > 1 && (
              <GenericCard
                className={'inner-list-wrapper p-0 p-lg-10 mt-10 mt-lg-0'}
                variant={'white'}
              >
                <h5 className={'mb-5 text-fs-6 fw-normal'}>
                  {'Persona Switcher'}
                </h5>
                <section className="inner-list scroll-show">
                  {data.personas &&
                    showPersonaMenu &&
                    personasCategories.map((category: string, idx: number) => (
                      <Container
                        key={idx}
                        className={`px-0 ${idx !== 0 ? 'mt-6' : ''} `}
                      >
                        <span className={'fw-bold text-fs-6 lh-xs'}>
                          {category}
                        </span>
                        <FormLabel
                          className={'w-100 mb-0'}
                          onClick={() => setShow(false)}
                          key={idx}
                        >
                          {data?.personas
                            ?.filter((x) => x.category === category)
                            .map((persona, idx) => {
                              return (
                                <Container
                                  className={'px-0'}
                                  key={`${category}-${idx}`}
                                >
                                  <div
                                    onClick={() => {
                                      if (persona.ready)
                                        handleChange(persona.id);
                                    }}
                                    className="d-flex align-items-center inner-item mt-1"
                                  >
                                    <div
                                      className={`d-flex flex-column ${
                                        selectedPersona === persona.id
                                          ? 'text-primary'
                                          : ''
                                      } ${
                                        persona.ready ? '' : 'text-light-grey'
                                      }`}
                                    >
                                      <span className={'fw-light'}>
                                        {persona.title}
                                      </span>
                                      <span
                                        className={`fw-light text-caption ${
                                          selectedPersona === persona.id
                                            ? 'text-primary'
                                            : 'text-light-grey'
                                        }`}
                                      >
                                        {persona.group}
                                      </span>
                                    </div>
                                    {selectedPersona === `${persona.id}` && (
                                      <div className={'ms-auto check-mark'}>
                                        <FontAwesomeIcon
                                          icon={light('check')}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </Container>
                              );
                            })}
                        </FormLabel>
                      </Container>
                    ))}
                </section>
              </GenericCard>
            )}
          </section>
          <div
            className={'click-area text-dark-gray text-fs-6 fw-light mt-3'}
            onClick={() => {
              logout();
            }}
          >
            <span className="d-inline-block w-100 pt-7">{'Log out'}</span>
          </div>
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger={'click'}
      placement={placement}
      overlay={profilePopoverBody}
      show={show}
      onToggle={handleToggle}
      rootClose={show}
    >
      <div className={'d-flex'}>{children}</div>
    </OverlayTrigger>
  );
};

export default FilerPopover;
